import { useState } from 'react';

import { Lock, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthenticatedRoutes } from '@/router/routes';
import { formatDisplayDate } from '@/utils/date-formatters';

import { useDeleteReport } from '../hooks/use-delete-report';
import { Report, ReportScope } from '../types';

interface ReportCardProps {
  report: Report;
}

export const ReportCard = ({ report }: ReportCardProps) => {
  const { t } = useTranslation('authenticated.reports');
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { mutate: deleteReport, isPending } = useDeleteReport();

  const handleCardClick = () => {
    navigate(AuthenticatedRoutes.ReportDetails.replace(':reportId', report.id));
  };

  const handleDelete = () => {
    deleteReport(report.id, {
      onSuccess: () => setConfirmOpen(false),
    });
  };

  const formattedDate = formatDisplayDate(new Date(report.createdAt));
  const isSystem = report.scope === ReportScope.SYSTEM;

  return (
    <>
      <Card className="cursor-pointer transition-shadow hover:shadow-md" onClick={handleCardClick}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base">{report.name}</CardTitle>
            {isSystem ? (
              <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={e => {
                  e.stopPropagation();
                  setConfirmOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{formattedDate}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('page.elements', { count: report.elements.length })}
          </p>
        </CardContent>
      </Card>

      {!isSystem && (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('deleteReport.title')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {t('deleteReport.confirm', { name: report.name })}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={isPending}>
                {t('common.delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
